import { Injectable } from '@angular/core';

export type ApiResponse<T = any> = {
  ok: boolean;
  status: number;
  data: T | null;
  error: string | null;
};

export type Params = Record<string, string | number>;

@Injectable({ providedIn: 'root' })
export class MobileRestService {


  buildApis(baseUrl: string) {
    const wd         = `${baseUrl}/wd/hub`;
    const deviceFarm = `${baseUrl}/device-farm`;

    return {

      // ── Session ──────────────────────────────────────────────────────────────
      createSession:  { url: `${wd}/session`,                                                        method: 'POST'   },
      deleteSession:  { url: `${wd}/session/{sessionId}`,                                            method: 'DELETE' },
      getSession:     { url: `${wd}/session/{sessionId}`,                                            method: 'GET'    },

      // ── Streaming ────────────────────────────────────────────────────────────
      // WebSocket URL — not a fetch call, provided here for reference/documentation
      streamSocket:   { url: `wss://${baseUrl.replace(/^https?:\/\//, '')}/android-stream/{sessionId}`, method: 'WS' },

      // ── Device resolution ────────────────────────────────────────────────────
      windowRect:     { url: `${wd}/session/{sessionId}/window/rect`,                                method: 'GET'    },
      wmSize:         { url: `${wd}/session/{sessionId}/execute`,                                    method: 'POST'   },

      // ── Actions (tap / swipe / key etc.) ────────────────────────────────────
      sendActions:    { url: `${wd}/session/{sessionId}/actions`,                                    method: 'POST'   },
      deleteActions:  { url: `${wd}/session/{sessionId}/actions`,                                    method: 'DELETE' },
      pressKey:       { url: `${wd}/session/{sessionId}/actions`,                                    method: 'POST'   },
      pressKeycode:   { url: `${wd}/session/{sessionId}/appium/device/press_keycode`,                method: 'POST'   },

      // ── Page source (for element locator parsing) ─────────────────────────────
      pageSource:     { url: `${wd}/session/{sessionId}/source`,                                     method: 'GET'    },
      flutterSource:  { url: `${wd}/session/{sessionId}/appium/app/flutter_page_source`,             method: 'GET'    },

      // ── Contexts (WebView switching) ──────────────────────────────────────────
      getContexts:    { url: `${wd}/session/{sessionId}/contexts`,                                   method: 'GET'    },
      setContext:     { url: `${wd}/session/{sessionId}/context`,                                    method: 'POST'   },

      // ── Script execution ──────────────────────────────────────────────────────
      executeSync:    { url: `${wd}/session/{sessionId}/execute/sync`,                               method: 'POST'   },

      // ── App management ────────────────────────────────────────────────────────
      installApp:     { url: `${wd}/session/{sessionId}/appium/device/install_app`,                  method: 'POST'   },
      uninstallApp:   { url: `${wd}/session/{sessionId}/appium/device/remove_app`,                   method: 'POST'   },
      isAppInstalled: { url: `${wd}/session/{sessionId}/appium/device/app_installed`,                method: 'POST'   },
      launchApp:      { url: `${wd}/session/{sessionId}/appium/app/launch`,                          method: 'POST'   },
      closeApp:       { url: `${wd}/session/{sessionId}/appium/app/close`,                           method: 'POST'   },
      resetApp:       { url: `${wd}/session/{sessionId}/appium/app/reset`,                           method: 'POST'   },
      startActivity:  { url: `${wd}/session/{sessionId}/appium/device/start_activity`,               method: 'POST'   },

      // ── File management ───────────────────────────────────────────────────────
      pushFile:       { url: `${wd}/session/{sessionId}/appium/device/push_file`,                    method: 'POST'   },

      // ── APK upload (device-farm custom endpoint) ──────────────────────────────
      uploadApk:      { url: `${deviceFarm}/apps`,                                                   method: 'POST'   },
      listApps:       { url: `${deviceFarm}/apps`,                                                   method: 'GET'    },
      deleteApk:      { url: `${deviceFarm}/apps/{appName}`,                                         method: 'DELETE' },

    } as const;
  }

  // ── URL interpolation ──────────────────────────────────────────────────────────

  private applyParams(url: string, params: Params): string {
    return url.replace(/\{(\w+)\}/g, (_, key) =>
      key in params ? encodeURIComponent(String(params[key])) : `{${key}}`
    );
  }

  private async rawRequest<T>(
    url: string,
    method: string,
    body?: unknown
  ): Promise<ApiResponse<T>> {

    if (method === 'WS') {
      console.warn(`[MobileRestService] WebSocket endpoint — use WebSocket() directly.`);
      return { ok: false, status: 0, data: null, error: 'WebSocket endpoint — not a fetch call.' };
    }

    const init: RequestInit = {
      method,
      headers: { 'Content-Type': 'application/json' },
      ...(body !== undefined ? { body: JSON.stringify(body) } : {})
    };

    try {
      const res = await fetch(url, init);
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        return { ok: false, status: res.status, data: null, error: text || `HTTP ${res.status}` };
      }
      if (res.status === 204) {
        return { ok: true, status: 204, data: null, error: null };
      }
      const data: T = await res.json();
      return { ok: true, status: res.status, data, error: null };
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Network error';
      return { ok: false, status: 0, data: null, error: msg };
    }
  }

  call<T = any>(
    baseUrl: string,
    apiKey: keyof ReturnType<MobileRestService['buildApis']>,
    params: Params = {},
    body?: unknown
  ): Promise<ApiResponse<T>> {
    const apis   = this.buildApis(baseUrl);
    const def    = apis[apiKey];
    const url    = this.applyParams(def.url, params);
    const method = def.method;
    return this.rawRequest<T>(url, method, body);
  }


  async raw<T = any>(
    url: string,
    method: 'GET' | 'POST' | 'PUT' | 'DELETE' = 'GET',
    body?: unknown
  ): Promise<ApiResponse<T>> {
    return this.rawRequest<T>(url, method, body);
  }

  getStreamUrl(baseUrl: string, sessionId: string): string {
    const apis = this.buildApis(baseUrl);
    return this.applyParams(apis.streamSocket.url, { sessionId });
  }
}